import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { Text } from "@nbs/ui";
import { toLandCoverChart } from "@/features/analytics/utils/land-cover";
import { NbsAreaChart } from "@/features/charts/NbsAreaChart";
import { GuineaLandscapeMap } from "@/features/story/components/GuineaLandscapeMap";
import { filterLandCoverRowsByPublishedYears } from "@/features/story/data/services";
import { HUB_OVERVIEW_QUERY } from "../api/documents";
import { hubPeriodsForLandscape, parsePeriodSelect } from "../state/hub-scope";
import { useHubScope } from "../state/HubScopeContext";
import { HubPageTitle } from "../components/HubPageTitle";

const AREA_COLORS = [
  "#2D5A27",
  "#3D87B8",
  "#C47B2C",
  "#52B788",
  "#B08968",
  "#E8A838",
  "#ADC178",
  "#6B8F71",
];

export function HubOverviewPage() {
  const { t } = useTranslation();
  const { landscapeId, landscapeLabel, period, periodId, setPeriodId, queryVars } = useHubScope();

  const { data, loading, error, refetch } = useQuery(HUB_OVERVIEW_QUERY, {
    variables: { ...queryVars },
    fetchPolicy: "cache-and-network",
  });

  const landCover = data?.landCoverSeries?.[0];
  const chart = useMemo(() => {
    const raw = toLandCoverChart(landCover?.points ?? []);
    return {
      ...raw,
      byYear: filterLandCoverRowsByPublishedYears(raw.byYear, landscapeId),
    };
  }, [landCover, landscapeId]);
  const seriesKeys = chart.classes.slice(0, 8);
  const periodOptions = hubPeriodsForLandscape(landscapeId);

  return (
    <div className="nbs-hub-page" data-testid="hub-overview-page">
      <section className="nbs-hub-section">
        <HubPageTitle
          trailing={
            <select
              className="nbs-hub__plain-select"
              data-testid="hub-period-select"
              aria-label={t("hub.periodAria")}
              value={periodId}
              onChange={(e) => {
                const next = parsePeriodSelect(e.target.value);
                if (next) setPeriodId(next);
              }}
            >
              {periodOptions.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
            </select>
          }
        >
          {t("hub.overview.title")}
        </HubPageTitle>

        {error ? (
          <div className="nbs-hub-banner nbs-hub-banner--danger" role="alert">
            <Text>{t("hub.overview.loadError")}</Text>
            <button type="button" className="nbs-hub-btn" onClick={() => void refetch()}>
              {t("hub.overview.retry")}
            </button>
          </div>
        ) : null}

        <div className="nbs-hub-overview-grid">
          <article className="nbs-hub-card nbs-hub-card--chart">
            <header className="nbs-hub-card__head">
              <h3>{t("hub.overview.trendChartTitle")}</h3>
              <p>
                {t("hub.overview.trendChartSub", {
                  place: landscapeLabel,
                  period: period.label,
                })}
              </p>
            </header>
            <div className="nbs-hub-card__body">
              {loading && !landCover ? (
                <p className="nbs-hub-muted">{t("hub.overview.loading")}</p>
              ) : chart.byYear.length === 0 ? (
                <p className="nbs-hub-muted">{t("hub.overview.noLandCover")}</p>
              ) : (
                <NbsAreaChart
                  data={chart.byYear}
                  xKey="year"
                  seriesKeys={seriesKeys}
                  height={300}
                  unit={landCover?.unit}
                  colors={AREA_COLORS}
                />
              )}
            </div>
          </article>

          <article className="nbs-hub-card nbs-hub-card--map">
            <header className="nbs-hub-card__head">
              <h3>{t("hub.overview.mapTitle", { year: period.yearTo })}</h3>
              <p>{landscapeLabel}</p>
            </header>
            <div className="nbs-hub-mini-map">
              <GuineaLandscapeMap
                selectedId={landscapeId}
                onSelect={() => undefined}
                interactive={false}
                showCaption={false}
              />
            </div>
          </article>
        </div>
      </section>
    </div>
  );
}
